import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import type { AutomationBannerCopy } from './automationTabConstants';

export interface AutomationFirstTimeBannerProps {
  copy: AutomationBannerCopy;
  onPrimaryAction: () => void;
  onDismiss: () => void;
}

/** UI-04 — State-aware automation guidance banner */
const AutomationFirstTimeBanner: React.FC<AutomationFirstTimeBannerProps> = ({
  copy,
  onPrimaryAction,
  onDismiss,
}) => {
  const muiTheme = useMuiTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${muiTheme.palette.primary.main}`,
        backgroundColor: muiTheme.palette.mode === 'dark'
          ? 'rgba(25, 118, 210, 0.12)'
          : 'rgba(25, 118, 210, 0.06)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <SmartToyOutlinedIcon sx={{ color: 'primary.main', fontSize: 28, mt: 0.25 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {copy.title}
          </Typography>
          <Box sx={{ mb: 2 }}>
            {copy.bodyLines.map((line) => (
              <Typography
                key={line}
                variant="body2"
                color="text.secondary"
                sx={{ mb: copy.bodyLines.length > 1 ? 1 : 0, '&:last-of-type': { mb: 0 } }}
              >
                {line}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={onPrimaryAction}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {copy.primaryButton}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onDismiss}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {copy.dismissButton}
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default AutomationFirstTimeBanner;
